import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Certificates.css';

const Certificates = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        const endpoint = isAdmin
          ? '/certificates/admin'
          : '/certificates/my-certificates';
        const response = await api.get(endpoint);
        setCertificates(response.data);
      } catch (error) {
        console.error('Error fetching certificates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();
  }, [isAdmin]);

  const handleDownload = async (certId) => {
    try {
      const response = await api.get(`/certificates/download/${certId}`, {
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'certificate.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate. Please try again.');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const renderLearnerView = () => (
    <>
      <section className="certificates-hero">
        <div className="hero-content">
          <p className="eyebrow">Credentials</p>
          <h1>My Certificates</h1>
          <p>
            Showcase what you have accomplished. Download, print, or share your
            certificates with your team.
          </p>
        </div>
        <div className="hero-card">
          <span className="label">Certificates earned</span>
          <p className="hero-value">{certificates.length}</p>
          <p className="hero-note">
            {certificates.length > 0
              ? 'Keep the streak going!'
              : 'Complete your first course to earn one.'}
          </p>
        </div>
      </section>

      {certificates.length === 0 ? (
        <div className="empty-state">
          <p>You haven't earned any certificates yet.</p>
          <p>Complete courses to earn certificates!</p>
        </div>
      ) : (
        <div className="certificates-grid">
          {certificates.map((cert) => (
            <article key={cert.id} className="certificate-card">
              <header>
                <div className="certificate-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 15l-3 3m0 0l-3-3m3 3V9m12 3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="label">Course</p>
                  <h3>{cert.course_title}</h3>
                  <p className="instructor">{cert.instructor_name}</p>
                </div>
                <span className="badge">Verified</span>
              </header>
              <div className="certificate-meta">
                <div>
                  <span className="meta-label">Issued</span>
                  <p>{new Date(cert.issued_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="meta-label">Certificate #</span>
                  <p>{cert.certificate_number}</p>
                </div>
                <div>
                  <span className="meta-label">Verification</span>
                  <p>{cert.verification_code}</p>
                </div>
              </div>
              <div className="certificate-actions">
                <button
                  onClick={() => handleDownload(cert.id)}
                  className="btn-primary"
                >
                  Download PDF
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );

  const renderAdminView = () => (
    <>
      <section className="certificates-hero admin">
        <div className="hero-content">
          <p className="eyebrow">Administration</p>
          <h1>Certificate Management</h1>
          <p>Review issued certificates, verify records, and reissue PDFs.</p>
        </div>
        <div className="hero-card">
          <span className="label">Total issued</span>
          <p className="hero-value">{certificates.length}</p>
          <p className="hero-note">Most recent entries appear first.</p>
        </div>
      </section>

      <div className="admin-certificates-table">
        <div className="table-header">
          <span>Student</span>
          <span>Course</span>
          <span>Issued</span>
          <span>Certificate #</span>
          <span>Actions</span>
        </div>
        {certificates.length === 0 ? (
          <div className="empty-state">
            <p>No certificates issued yet.</p>
          </div>
        ) : (
          certificates.map((cert) => (
            <div key={cert.id} className="table-row">
              <div className="student-cell">
                <strong>{cert.student_name}</strong>
                <span>{cert.student_email}</span>
              </div>
              <div className="course-cell">
                <p className="course-title">{cert.course_title}</p>
                <span>{cert.instructor_name}</span>
              </div>
              <span className="issued-cell">
                {new Date(cert.issued_at).toLocaleDateString()}
              </span>
              <span className="certificate-cell">{cert.certificate_number}</span>
              <div className="actions-cell">
                <button
                  className="btn-secondary"
                  onClick={() => handleDownload(cert.id)}
                >
                  Download
                </button>
                <span className="verification-code">{cert.verification_code}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <div className={`page certificates-page ${isAdmin ? 'manage-view' : ''}`}>
      <div className="container">
        {isAdmin ? renderAdminView() : renderLearnerView()}
      </div>
    </div>
  );
};

export default Certificates;
